import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import type { ReactNode } from "react";
import { LoadingSpinner } from "@/core/components/loading-spinner";
import { Button } from "@/core/components/ui/button";
import { RUN_STATUS } from "@/profitability/constants";
import type { ProfitabilityRun } from "@/profitability/types";
import { useRerunReport } from "@/profitability/use-rerun-report";
import { RunStatePanel } from "./run-state-panel";
export const RunContent = ({
  businessId,
  run,
  children,
}: {
  businessId: string;
  run: ProfitabilityRun;
  children: ReactNode;
}) => {
  const { rerun, rerunning } = useRerunReport(businessId, run);
  if (run.status === RUN_STATUS.failed)
    return (
      <RunStatePanel
        icon={<ExclamationTriangleIcon className="h-10 w-10 text-red-50" />}
        title="This report could not be built"
        body={
          run.error?.message ??
          "Something went wrong while building this report."
        }
        action={
          <Button size="sm" loading={rerunning} onClick={rerun}>
            Try these dates again
          </Button>
        }
      />
    );

  if (run.status !== RUN_STATUS.completed)
    return (
      <RunStatePanel
        icon={<LoadingSpinner containerClassName="h-10 w-auto" />}
        title={
          run.status === RUN_STATUS.pending
            ? "Your report is queued"
            : "Building your report"
        }
        body="Volca is pulling this range from ServiceTitan and running the numbers. You can leave this page. The report keeps building."
      />
    );

  if (!run.workbook_available)
    return (
      <RunStatePanel
        icon={<ExclamationTriangleIcon className="h-10 w-10 text-gold-50" />}
        title="The results are no longer available"
        body="This report finished, but its workbook has since been removed. Building these dates again produces a fresh copy."
        action={
          <Button size="sm" loading={rerunning} onClick={rerun}>
            Build it again
          </Button>
        }
      />
    );

  return children;
};
